import { useEffect } from "react";
import PropTypes from "prop-types";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
  useParams,
} from "react-router";
import { Toaster } from "react-hot-toast";

import { useAuthStore } from "./store/authStore.js";
import LoadingSpinner from "./components/LoadingSpinner.jsx";
import api from "./utilities/api.js";
import { useSessionHeartbeat } from "./hooks/useSessionHeartbeat.js";

import Auth from "./layouts/Auth.jsx";
import Fields from "./layouts/Fields.jsx";

import Navbar from "./components/Navbar.jsx";
import Home from "./components/Home.jsx";
import FieldList from "./components/FieldList.jsx";
import Error from "./components/Error.jsx";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import SessionExpiredWatcher from "./components/SessionExpiredWatcher.jsx";

import FieldCreatePage from "./pages/FieldCreatePage.jsx";
import FieldDetailPage from "./pages/FieldDetailPage.jsx";
import FieldDraftEditPage from "./pages/FieldDraftEditPage.jsx";
import ForgotPasswordPage from "./pages/ForgotPasswordPage.jsx";
import ResetPasswordPage from "./pages/ResetPasswordPage.jsx";
import VerifyEmailPage from "./pages/VerifyEmailPage.jsx";
import DashboardPage from "./pages/DashboardPage.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import SignUpPage from "./pages/SignUpPage.jsx";
import ToolsPage from "./pages/ToolsPage.jsx";
import AdminPage from "./pages/AdminPage.jsx";

import { loader as detailLoader } from "./utilities/detailLoader.js";
import { loader as recordListLoader } from "./utilities/allRecordsLoader.js";
import { action as deleteAction } from "./utilities/deleteAction.js";
import { action as editAction } from "./utilities/editAction.js";
import { loader as draftLoader } from "./utilities/draftLoader.js";
import { action as draftEditAction } from "./utilities/draftEditAction.js";

// Protect routes that require authentication

const ProtectedDraftRoute = ({ children }) => {
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const isMember = useAuthStore((state) => state.isMember);
  const { id } = useParams();

  if (!isAdmin() && !isMember()) {
    return <Navigate to={`/list/${id}`} replace />;
  }
  return children;
};

ProtectedDraftRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const ProtectedCreateRoute = ({ children }) => {
  const isAdmin = useAuthStore((state) => state.isAdmin);

  if (!isAdmin()) {
    return <Navigate to="/list" replace />;
  }
  return children;
};

ProtectedCreateRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <>
        <Navbar />
        <Outlet />
        <Toaster />
        <SessionExpiredWatcher />
      </>
    ),
    errorElement: <Error />,
    children: [
      { path: "/", index: true, element: <Home /> },
      {
        path: "auth",
        element: <Auth />,
        // errorElement: <Error />,
        children: [
          {
            index: true,
            element: <LoginPage />,
            // action: authAction,
          },
          {
            path: "signup",
            element: <SignUpPage />,
          },
          { path: "verify", element: <VerifyEmailPage /> },
          {
            path: "forgotPassword",
            element: <ForgotPasswordPage />,
          },
          {
            path: "reset-password/:token",
            element: <ResetPasswordPage />,
          },
        ],
      },
      {
        path: "/dashboard",
        element: <DashboardPage />,
      },
      {
        path: "list",
        element: <Fields />,
        // errorElement: <Error />,
        children: [
          {
            path: "",
            element: <FieldList />,
            loader: recordListLoader,
          },
          {
            path: ":id",
            id: "field-detail",
            loader: detailLoader,
            children: [
              {
                index: true,
                element: <FieldDetailPage />,
                action: deleteAction,
              },
              {
                path: "draft-edit",
                element: (
                  <ProtectedDraftRoute>
                    <FieldDraftEditPage />
                  </ProtectedDraftRoute>
                ),
                loader: draftLoader,
                action: draftEditAction,
              },
            ],
          },
          {
            path: "create",
            element: (
              <ProtectedCreateRoute>
                <FieldCreatePage />
              </ProtectedCreateRoute>
            ),
            action: editAction,
          },
        ],
      },
      {
        element: <ProtectedRoute requiredRoles={["admin"]} />,
        children: [
          {
            path: "admin",
            element: <AdminPage />,
          },
        ],
      },
      {
        element: <ProtectedRoute requiredRoles={["admin", "member", "user"]} />,
        children: [
          {
            path: "tools",
            element: <ToolsPage />,
          },
        ],
      },
    ],
  },
]);

const App = () => {
  const isCheckingAuth = useAuthStore((state) => state.isCheckingAuth);
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Detect session expiry surfaced by any authenticated request (in addition
  // to the activity heartbeat below), and flag it for SessionExpiredWatcher.
  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response?.status === 401 &&
          useAuthStore.getState().isAuthenticated
        ) {
          useAuthStore.getState().flagSessionExpired();
        }
        return Promise.reject(error);
      },
    );
    return () => api.interceptors.response.eject(interceptorId);
  }, []);

  useSessionHeartbeat();

  if (isCheckingAuth) return <LoadingSpinner />;

  return <RouterProvider router={router} />;
};

export default App;
